import { Nullable } from '../../common/domain/types/types';

export type MasterDataConfigurationData = {
  display_service_address_in_task: boolean;
  display_postal_address_in_immediate_task: boolean;
  display_postal_address_in_planned_task: boolean;
  display_ambit_in_immediate_task: boolean;
  display_ambit_in_planned_task: boolean;
  display_description_in_immediate_task: boolean;
  display_description_in_planned_task: boolean;
  display_task_type_in_create_task: boolean;
  max_attachments: number;
  max_attachment_size: number;
  max_request_size: number;
  can_create_tasks: boolean;
  create_task_work_types: Array<{
    id: string;
    name: string;
  }>;
  observations_for_client_inline: boolean;
  presence_check?: Nullable<{
    enabled: boolean;
    preselected_ambit_id: Nullable<string>;
    delay_seconds: Nullable<number>;
    response_timeout_seconds: Nullable<number>;
  }>;
};

export interface UserPresenceConfiguration {
  enabled: boolean;
  preselected_ambit_id: Nullable<string>;
  delay_seconds: Nullable<number>;
  response_timeout_seconds: Nullable<number>;
}

export const DEFAULT_USER_PRESENCE_CONFIGURATION = {
  delay_seconds: 10 * 1000, // 10s
  response_timeout_seconds: 10 * 1000, // 10 s
};

export class MasterDataConfiguration {
  constructor(public readonly value: MasterDataConfigurationData) {}

  isDisplayServiceAddressInTask(): boolean {
    return this.value.display_service_address_in_task;
  }

  isDisplayPostalAddressInImmediateTask(): boolean {
    return this.value.display_postal_address_in_immediate_task;
  }

  isDisplayPostalAddressInPlannedTask(): boolean {
    return this.value.display_postal_address_in_planned_task;
  }

  isDisplayAmbitInImmediateTask(): boolean {
    return this.value.display_ambit_in_immediate_task;
  }

  isDisplayAmbitInPlannedTask(): boolean {
    return this.value.display_ambit_in_planned_task;
  }

  isDisplayDescriptionInImmediateTask(): boolean {
    return this.value.display_description_in_immediate_task;
  }

  isDisplayDescriptionInPlannedTask(): boolean {
    return this.value.display_description_in_planned_task;
  }

  isDisplayTaskTypeInCreateTask(): boolean {
    return this.value.display_task_type_in_create_task;
  }

  isDisplayInlineObservationsForClient(): boolean {
    return this.value.observations_for_client_inline;
  }

  getMaxAttachments(): number {
    return this.value.max_attachments;
  }

  getMaxAttachmentSize(): number {
    return this.value.max_attachment_size;
  }

  getMaxRequestSize(): number {
    return this.value.max_request_size;
  }

  getCreateTaskWorkTypes(): Array<{ id: string; name: string }> {
    return this.value.create_task_work_types;
  }

  canCreateTasks(): boolean {
    return this.value.can_create_tasks;
  }

  getPresenceConfiguration(): UserPresenceConfiguration {
    const enabled = this.value?.presence_check?.enabled ?? false;
    if (!this.value || !enabled) {
      return {
        enabled: false,
        preselected_ambit_id: this.value?.presence_check?.preselected_ambit_id ?? null,
        delay_seconds: null,
        response_timeout_seconds: null,
      };
    }

    return {
      enabled,
      preselected_ambit_id: this.value.presence_check?.preselected_ambit_id,
      delay_seconds: this.value.presence_check?.delay_seconds ?? DEFAULT_USER_PRESENCE_CONFIGURATION.delay_seconds,
      response_timeout_seconds:
        this.value.presence_check?.response_timeout_seconds ??
        DEFAULT_USER_PRESENCE_CONFIGURATION.response_timeout_seconds,
    };
  }
}
